import { createContext, useContext, useEffect, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // ISO string of when the current session's cookie/JWT expires. Null when
  // signed out. Updated on login, /auth/me, and /auth/refresh. The
  // SessionTimeoutBanner subscribes to this to render the warning + run the
  // auto-logout timer.
  const [expiresAt, setExpiresAt] = useState(null);
  // Nonce bumped whenever the session is refreshed so timer effects re-run.
  const sessionNonce = useRef(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        setExpiresAt(data?.token_expires_at || null);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password, captcha = null) => {
    try {
      const payload = { email, password };
      if (captcha?.id && captcha?.answer) {
        payload.captcha_id = captcha.id;
        payload.captcha_answer = captcha.answer;
      }
      const { data } = await api.post("/auth/login", payload);
      setUser(data.user);
      setExpiresAt(data.expires_at || data.user?.token_expires_at || null);
      sessionNonce.current += 1;
      return { ok: true };
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 400 && detail?.captcha_required) {
        return {
          ok: false,
          captcha: { id: detail.captcha_id, question: detail.question },
          error: detail.message || "Please solve the captcha to continue.",
        };
      }
      return { ok: false, error: formatErr(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    try { localStorage.removeItem("gtes_token"); } catch (_) { /* storage disabled */ }
    setUser(false);
    setExpiresAt(null);
  };

  const refreshSession = async () => {
    try {
      const { data } = await api.post("/auth/refresh");
      setExpiresAt(data.expires_at);
      sessionNonce.current += 1;
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatErr(e) };
    }
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, setUser, expiresAt, refreshSession, sessionNonce: sessionNonce.current }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
